import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Upload, Trash2 } from 'lucide-react';
import API_URL from '../config';
import AddDriverModal from '../components/Drivers/AddDriverModal';
import { Badge, Button } from '../components/ui';
import { Skeleton } from '../components/ui/Skeleton';
import {
  formatVisionDate,
  getVisionBadgeState,
  hasPendingVisionVerification,
} from '../utils/documentVision';
import '../App.css';

const DQ_TOTAL = 9;

const DQ_DOCUMENT_TYPES = [
  'Application for Employment',
  "Commercial Driver's License",
  "Medical Examiner's Certificate",
  'Motor Vehicle Record',
  'Road Test Certificate',
  'Previous Employment Verification',
  'Pre-Employment Drug Test',
  'Annual MVR Review',
  'Previous D&A Violation History',
];

const ALLOWED_UPLOAD_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

const pageMotion = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { type: 'spring', stiffness: 90, damping: 22 },
};

function formatDisplayDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function pickLatestDocForType(documents, documentType) {
  if (!Array.isArray(documents)) return null;
  return documents.find((d) => d.documentType === documentType) || null;
}

function getVaultRowVariant(doc) {
  if (!doc) return 'missing';
  if (!doc.visionCheckedAt) return 'pending';
  if (doc.visionVerified === true) return 'verified';
  return 'unverified';
}

function DriverProfileSkeleton() {
  return (
    <div className="page-container fleet-driver-profile">
      <Skeleton width={140} height={20} />
      <div className="fleet-driver-profile__header" style={{ marginTop: 20 }}>
        <Skeleton width={280} height={36} />
        <Skeleton width={100} height={28} />
      </div>
      <div className="fleet-driver-profile__card ui-card" style={{ marginTop: 24 }}>
        <Skeleton width={160} height={22} />
        <Skeleton width="100%" height={12} style={{ marginTop: 16 }} />
        <div className="fleet-driver-profile__stats" style={{ marginTop: 20 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} width="100%" height={48} />
          ))}
        </div>
      </div>
      <div className="fleet-driver-profile__card ui-card" style={{ marginTop: 20 }}>
        <Skeleton width={120} height={22} />
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} width="100%" height={18} style={{ marginTop: 12 }} />
        ))}
      </div>
      <div className="fleet-driver-profile__vault-skeleton" style={{ marginTop: 20 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} width="100%" height={72} style={{ marginBottom: 12 }} />
        ))}
      </div>
    </div>
  );
}

function DriverVaultVisionBadge({ doc }) {
  const state = getVisionBadgeState(doc);
  const label =
    state.status === 'verified'
      ? '✓ AI Verified'
      : state.status === 'unverified'
        ? '⚠ Unverified'
        : 'Verifying...';

  return (
    <motion.div
      className="vision-verification-badge"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={pageMotion.transition}
      layout
    >
      <Badge variant={state.variant} className="vision-verification-badge__pill">
        {label}
      </Badge>
      {state.status === 'unverified' && doc?.visionFailureReason ? (
        <p className="vision-verification-badge__reason">{doc.visionFailureReason}</p>
      ) : null}
      {doc?.visionExtractedExpiry ? (
        <p className="vision-verification-badge__auto-expiry">
          Expiry auto-detected: {formatVisionDate(doc.visionExtractedExpiry)}
        </p>
      ) : null}
    </motion.div>
  );
}

function getDriverDisplayName(driver) {
  if (!driver) return 'Driver';
  const name = (driver.name || '').trim();
  return name || 'Driver';
}

export default function FleetDriverProfilePage() {
  const { driverId: fleetDriverId, id: legacyDriverId } = useParams();
  const driverId = fleetDriverId || legacyDriverId;
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [dqStatus, setDqStatus] = useState(null);
  const [driverDocuments, setDriverDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [vaultError, setVaultError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [openUploadType, setOpenUploadType] = useState(null);
  const [uploadingType, setUploadingType] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadErrors, setUploadErrors] = useState({});
  const [deletingDocId, setDeletingDocId] = useState(null);
  const fileInputRef = useRef(null);

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }),
    [],
  );

  const refreshDqStatus = useCallback(async () => {
    const dqRes = await axios.get(
      `${API_URL}/api/driver-documents/${driverId}/dq-status`,
      authHeaders,
    );
    setDqStatus(dqRes.data);
  }, [authHeaders, driverId]);

  const fetchDriverDocuments = useCallback(async () => {
    const docsRes = await axios.get(
      `${API_URL}/api/driver-documents/${driverId}`,
      authHeaders,
    );
    setDriverDocuments(docsRes.data || []);
  }, [authHeaders, driverId]);

  const refreshVaultAndDq = useCallback(async () => {
    await Promise.all([fetchDriverDocuments(), refreshDqStatus()]);
  }, [fetchDriverDocuments, refreshDqStatus]);

  const loadPage = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      setVaultError('');

      const [driverRes, dqRes, docsRes] = await Promise.all([
        axios.get(`${API_URL}/api/drivers/${driverId}`, authHeaders),
        axios.get(`${API_URL}/api/driver-documents/${driverId}/dq-status`, authHeaders),
        axios.get(`${API_URL}/api/driver-documents/${driverId}`, authHeaders),
      ]);

      setDriver(driverRes.data);
      setDqStatus(dqRes.data);
      setDriverDocuments(docsRes.data || []);
    } catch (err) {
      console.error('Error loading fleet driver profile:', err);
      const status = err.response?.status;
      if (status === 403 || status === 404) {
        setError('Driver not found or you do not have access to this profile.');
      } else {
        setError('Failed to load driver profile. Please try again.');
      }
      setDriver(null);
      setDqStatus(null);
      setDriverDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [authHeaders, driverId]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const pendingVisionVerification = useMemo(
    () => hasPendingVisionVerification(driverDocuments),
    [driverDocuments],
  );

  useEffect(() => {
    if (!pendingVisionVerification || !driver) return undefined;
    const intervalId = setInterval(() => {
      fetchDriverDocuments().catch((err) => {
        console.error('Error polling driver documents:', err);
      });
    }, 5000);
    return () => clearInterval(intervalId);
  }, [pendingVisionVerification, driver, fetchDriverDocuments]);

  const handleUploadFile = async (documentType, file) => {
    if (!file) return;

    if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) {
      setUploadErrors((prev) => ({
        ...prev,
        [documentType]: 'Invalid file type. Please upload PDF, JPG, or PNG files only.',
      }));
      return;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadErrors((prev) => ({
        ...prev,
        [documentType]: 'File size exceeds 10MB limit.',
      }));
      return;
    }

    setUploadingType(documentType);
    setUploadProgress(0);
    setUploadErrors((prev) => ({ ...prev, [documentType]: '' }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', documentType);

      await axios.post(
        `${API_URL}/api/driver-documents/${driverId}/upload`,
        formData,
        {
          ...authHeaders,
          headers: {
            ...authHeaders.headers,
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const total = progressEvent.total || 1;
            setUploadProgress(Math.round((progressEvent.loaded * 100) / total));
          },
        },
      );

      setOpenUploadType(null);
      await refreshVaultAndDq();
    } catch (err) {
      console.error('Error uploading driver document:', err);
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Upload failed. Please try again.';
      setUploadErrors((prev) => ({
        ...prev,
        [documentType]: Array.isArray(message) ? message.join(', ') : message,
      }));
    } finally {
      setUploadingType(null);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Delete this document?')) return;

    try {
      setDeletingDocId(documentId);
      setVaultError('');
      await axios.delete(`${API_URL}/api/driver-documents/${documentId}`, authHeaders);
      await refreshVaultAndDq();
    } catch (err) {
      console.error('Error deleting driver document:', err);
      setVaultError('Failed to delete document. Please try again.');
    } finally {
      setDeletingDocId(null);
    }
  };

  const openUploadForType = (documentType) => {
    setOpenUploadType(documentType);
    setUploadErrors((prev) => ({ ...prev, [documentType]: '' }));
  };

  const triggerFilePicker = () => {
    fileInputRef.current?.click();
  };

  const compliance = driver?.compliance || dqStatus?.compliance;
  const isCompliant = (driver?.status || compliance?.status) === 'compliant';
  const displayName = getDriverDisplayName(driver);
  const documentsOnFile = dqStatus?.uploaded ?? 0;
  const progressPct = Math.round((documentsOnFile / DQ_TOTAL) * 100);

  if (isLoading) {
    return <DriverProfileSkeleton />;
  }

  if (error || !driver) {
    return (
      <div className="page-container fleet-driver-profile">
        <div className="fleet-driver-profile__error ui-card">
          <p>{error || 'Driver not found.'}</p>
          <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/drivers')}>
            Back to Drivers
          </Button>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="page-container fleet-driver-profile"
      initial={pageMotion.initial}
      animate={pageMotion.animate}
      transition={pageMotion.transition}
    >
      <Link to="/drivers" className="fleet-driver-profile__back">
        <ArrowLeft size={18} aria-hidden />
        Back to Drivers
      </Link>

      <header className="fleet-driver-profile__header">
        <h1 className="fleet-driver-profile__title">{displayName}</h1>
        <div className="fleet-driver-profile__header-meta">
          <Badge variant={isCompliant ? 'success' : 'danger'}>
            {isCompliant ? 'Compliant' : 'Non-Compliant'}
          </Badge>
          {driver.createdAt ? (
            <span className="fleet-driver-profile__hire-date">
              Added {formatDisplayDate(driver.createdAt)}
            </span>
          ) : null}
        </div>
      </header>

      <section className="fleet-driver-profile__card ui-card" aria-label="Driver compliance readiness">
        <div className="fleet-driver-profile__card-head">
          <h2 className="fleet-driver-profile__card-title">DQ File Readiness</h2>
          <Badge variant={isCompliant ? 'success' : 'danger'}>
            {isCompliant ? 'Compliant' : 'Non-Compliant'}
          </Badge>
        </div>

        <p className="fleet-driver-profile__progress-label">
          DQ File Progress: {documentsOnFile} of {DQ_TOTAL} documents
        </p>
        <div className="fleet-driver-profile__progress-track" aria-hidden>
          <motion.div
            className="fleet-driver-profile__progress-fill"
            initial={{ width: 0 }}
            animate={{ width: `${progressPct}%` }}
            transition={{ type: 'spring', stiffness: 80, damping: 20 }}
          />
        </div>

        <div className="fleet-driver-profile__stats">
          <div className="fleet-driver-profile__stat fleet-driver-profile__stat--verified">
            <span className="fleet-driver-profile__stat-label">Verified</span>
            <span className="fleet-driver-profile__stat-value">{dqStatus?.verified ?? 0}</span>
          </div>
          <div className="fleet-driver-profile__stat fleet-driver-profile__stat--missing">
            <span className="fleet-driver-profile__stat-label">Missing</span>
            <span className="fleet-driver-profile__stat-value">
              {dqStatus?.missing?.length ?? 0}
            </span>
          </div>
          <div className="fleet-driver-profile__stat fleet-driver-profile__stat--expiring">
            <span className="fleet-driver-profile__stat-label">Expiring Soon</span>
            <span className="fleet-driver-profile__stat-value">
              {dqStatus?.expiringSoon?.length ?? 0}
            </span>
          </div>
          <div className="fleet-driver-profile__stat">
            <span className="fleet-driver-profile__stat-label">Expired</span>
            <span className="fleet-driver-profile__stat-value">
              {dqStatus?.expired?.length ?? 0}
            </span>
          </div>
          <div className="fleet-driver-profile__stat">
            <span className="fleet-driver-profile__stat-label">Stale</span>
            <span className="fleet-driver-profile__stat-value">
              {dqStatus?.stale?.length ?? 0}
            </span>
          </div>
        </div>
      </section>

      <section className="fleet-driver-profile__card ui-card" aria-label="Driver information">
        <div className="fleet-driver-profile__card-head">
          <h2 className="fleet-driver-profile__card-title">Driver Information</h2>
          <Button variant="primary" onClick={() => setShowEditModal(true)}>
            Edit Driver
          </Button>
        </div>

        <p className="fleet-driver-profile__vault-subtitle">
          Employment record for a driver you employ. Your own CDL and medical card are
          managed in your profile settings.
        </p>

        <dl className="fleet-driver-profile__info-grid">
          <div className="fleet-driver-profile__info-row">
            <dt>Full name</dt>
            <dd>{driver.name}</dd>
          </div>
          <div className="fleet-driver-profile__info-row">
            <dt>CDL number + state</dt>
            <dd>
              {[driver.cdlNumber, driver.cdlState].filter(Boolean).join(' · ') || '—'}
            </dd>
          </div>
          <div className="fleet-driver-profile__info-row">
            <dt>CDL expiration</dt>
            <dd>{formatDisplayDate(driver.cdlExpiration)}</dd>
          </div>
          <div className="fleet-driver-profile__info-row">
            <dt>Medical card expiration</dt>
            <dd>{formatDisplayDate(driver.medicalCardExpiration)}</dd>
          </div>
          <div className="fleet-driver-profile__info-row">
            <dt>Clearinghouse registered</dt>
            <dd>{driver.clearinghouseRegistered ? 'Yes' : 'No'}</dd>
          </div>
          <div className="fleet-driver-profile__info-row">
            <dt>Clearinghouse last query</dt>
            <dd>{formatDisplayDate(driver.clearinghouseLastQuery)}</dd>
          </div>
          <div className="fleet-driver-profile__info-row">
            <dt>Drug test current</dt>
            <dd>{driver.drugTestCurrent ? 'Yes' : 'No'}</dd>
          </div>
          <div className="fleet-driver-profile__info-row fleet-driver-profile__info-row--full">
            <dt>Notes</dt>
            <dd>{driver.notes?.trim() ? driver.notes : '—'}</dd>
          </div>
        </dl>
      </section>

      <section className="fleet-driver-profile__vault" aria-label="Driver document vault">
        <h2 className="fleet-driver-profile__card-title">DQ File</h2>
        <p className="fleet-driver-profile__vault-subtitle">FMCSA 49 CFR Part 391</p>
        <p className="fleet-driver-profile__vault-count">
          {documentsOnFile} of {DQ_TOTAL} documents on file
        </p>

        {vaultError ? (
          <div className="fleet-driver-profile__vault-alert" role="alert">
            {vaultError}
          </div>
        ) : null}

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          className="fleet-driver-profile__vault-file-input"
          onChange={(e) => {
            const selected = e.target.files?.[0];
            if (selected && openUploadType) {
              handleUploadFile(openUploadType, selected);
            }
          }}
        />

        <ul className="fleet-driver-profile__vault-list">
          {DQ_DOCUMENT_TYPES.map((documentType, index) => {
            const doc = pickLatestDocForType(driverDocuments, documentType);
            const rowVariant = getVaultRowVariant(doc);
            const isUploadOpen = openUploadType === documentType;
            const isUploading = uploadingType === documentType;
            const rowUploadError = uploadErrors[documentType];

            return (
              <motion.li
                key={documentType}
                className={`fleet-driver-profile__vault-row ui-card fleet-driver-profile__vault-row--${rowVariant}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...pageMotion.transition, delay: index * 0.03 }}
              >
                <div className="fleet-driver-profile__vault-row-main">
                  <div className="fleet-driver-profile__vault-row-head">
                    <h3 className="fleet-driver-profile__vault-type">{documentType}</h3>
                    {!doc ? (
                      <Badge variant="danger">Missing</Badge>
                    ) : null}
                  </div>

                  {doc ? (
                    <div className="fleet-driver-profile__vault-meta">
                      <p>
                        <span className="fleet-driver-profile__vault-meta-label">Uploaded</span>
                        {formatDisplayDate(doc.uploadedAt)}
                      </p>
                      <p>
                        <span className="fleet-driver-profile__vault-meta-label">Expires</span>
                        {doc.expirationDate ? formatDisplayDate(doc.expirationDate) : '—'}
                      </p>
                      {doc.fileName ? (
                        <p className="fleet-driver-profile__vault-filename">{doc.fileName}</p>
                      ) : null}
                    </div>
                  ) : null}

                  {doc ? <DriverVaultVisionBadge doc={doc} /> : null}

                  {!doc ? (
                    <div className="fleet-driver-profile__vault-actions">
                      <Button
                        variant="primary"
                        size="sm"
                        icon={Upload}
                        onClick={() => openUploadForType(documentType)}
                        disabled={!!uploadingType}
                      >
                        Upload
                      </Button>
                    </div>
                  ) : (
                    <div className="fleet-driver-profile__vault-actions">
                      {doc.fileUrl ? (
                        <a
                          href={doc.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="fleet-driver-profile__vault-view"
                        >
                          View file
                        </a>
                      ) : null}
                      <Button
                        variant="destructive"
                        size="sm"
                        icon={Trash2}
                        onClick={() => handleDeleteDocument(doc.id)}
                        disabled={deletingDocId === doc.id}
                      >
                        Delete
                      </Button>
                    </div>
                  )}

                  {isUploadOpen || isUploading ? (
                    <div className="fleet-driver-profile__vault-upload">
                      {isUploading ? (
                        <>
                          <p className="fleet-driver-profile__vault-upload-status">
                            Uploading… {uploadProgress}%
                          </p>
                          <div
                            className="fleet-driver-profile__progress-track fleet-driver-profile__vault-progress"
                            aria-hidden
                          >
                            <div
                              className="fleet-driver-profile__progress-fill"
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="fleet-driver-profile__vault-upload-hint">
                            Choose a PDF, JPG, or PNG file to upload.
                          </p>
                          <Button
                            variant="secondary"
                            size="sm"
                            icon={Upload}
                            onClick={triggerFilePicker}
                          >
                            Select file
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setOpenUploadType(null);
                          setUploadErrors((prev) => ({ ...prev, [documentType]: '' }));
                        }}
                        disabled={isUploading}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : null}

                  {rowUploadError ? (
                    <p className="fleet-driver-profile__vault-row-error" role="alert">
                      {rowUploadError}
                    </p>
                  ) : null}
                </div>
              </motion.li>
            );
          })}
        </ul>
      </section>

      <AddDriverModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={loadPage}
        driver={driver}
      />
    </motion.div>
  );
}
